import React from "react";

import Selector, { type SelectorOption, type SingleValue } from "~/ui/selectors/Selector";

import styles from "./css/settings.module.css";

export type UIAutoRecordSetting = "all" | "organized" | "off" | "custom";

const AUTO_RECORD_LABELS: Record<UIAutoRecordSetting, JSX.Element> = {
	all: (
		<>
			All meetings
			<span className={styles.recommendedLabel}>Recommended</span>
		</>
	),
	organized: <>Meetings that I organize</>,
	off: <>Meetings I manually select</>,
	custom: <>Meetings following my custom rules</>,
};

const AUTO_RECORD_LABELS_WITHOUT_CUSTOM: Record<UIAutoRecordSetting, JSX.Element> = Object.fromEntries(
	Object.entries(AUTO_RECORD_LABELS).filter(([key]) => key !== "custom"),
) as Record<UIAutoRecordSetting, JSX.Element>;

/**
 * if the user is locked to a specific setting, disable all other settings. The key matching the value
 * should return false, since it should not be disabled.
 *
 * @param value - the value of the users' auto-record setting
 * @param locked - whether the setting is locked
 * @returns an object with the keys being all UIAutoRecordSetting options, and the values being whether they should be
 * disabled based on whether or not the settings are locked to a specific value.
 */
function getUIAutoRecordSettingsDisabledIfAccountSettingsLocked(
	value: UIAutoRecordSetting,
	locked: boolean,
): Record<UIAutoRecordSetting, boolean> {
	if (locked) {
		return {
			all: value !== "all",
			organized: value !== "organized",
			off: value !== "off",
			custom: value !== "custom",
		};
	}
	return {
		all: false,
		organized: false,
		off: false,
		custom: false,
	};
}

export default function AutoRecordingSettingsSelector({
	value,
	onChange,
	className,
	includeCustomOption,
	lockToValue,
}: {
	value: UIAutoRecordSetting;
	onChange: (value: UIAutoRecordSetting) => void;
	className?: string;
	includeCustomOption?: boolean;
	lockToValue?: boolean;
}) {
	const handleChange = React.useCallback(
		(selectedOption: SingleValue<SelectorOption>) => {
			if (!selectedOption) return;
			onChange(selectedOption.value as UIAutoRecordSetting);
		},
		[onChange],
	);

	const settingsDisabledByLockedDefaultAutoRecordSettings = React.useMemo(() => {
		return getUIAutoRecordSettingsDisabledIfAccountSettingsLocked(value, !!lockToValue);
	}, [lockToValue, value]);

	const labels = includeCustomOption ? AUTO_RECORD_LABELS : AUTO_RECORD_LABELS_WITHOUT_CUSTOM;
	if (lockToValue) {
		labels.all = <>All meetings</>;
	}

	return (
		<Selector
			className={className}
			value={{
				value,
				label: labels[value] as unknown as string,
			}}
			options={Object.entries(labels).map(([value, label]) => ({
				value,
				label: label as unknown as string,
				isDisabled: settingsDisabledByLockedDefaultAutoRecordSettings[value as UIAutoRecordSetting],
				hoverTooltipWhenDisabled: "This setting has been locked by your workspace admin and cannot be changed.",
			}))}
			onChange={handleChange}
		/>
	);
}
